import { useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useGetPlayersQuery, GET_PLAYERS_QUERY_KEY } from '@/state/queries/players/use-get-players';
import { GET_FLOW_QUERY_KEY } from '@/state/queries/flows/use-get-flow';
import { usePlayerSocket, type PlayerSubscriptionHandler } from '@/hooks/use-player-socket';
import { useNotifications } from '@/hooks/use-notifications';
import { useTranslations } from '@/i18n/use-translations.tsx';
import type { TranslationKey } from '@/i18n/translation-key.tsx';
import type { PlayerDto, PlayerType } from '@/types/dtos/player/response/player.dto';
import type { PlayerParentEntity } from '@/types/dtos/player/player-parent-entity';
import { requiresEventSlotForLiveBinding } from '@/helpers/live-binding-context.helper';
import { useEventStore } from '@/state/stores/use-event-store';
import { GET_AV_SOURCES_QUERY_KEY } from '@/state/queries/players/use-get-av-sources';
import { GET_SPACE_PLAYER_SETUP_QUERY_KEY } from '@/state/queries/players/use-get-space-player-setup';

export function useConfigurationPlayers({
  parentId,
  parentType,
  eventSlotId,
  enabled = true,
}: {
  parentId: string;
  parentType: PlayerParentEntity;
  eventSlotId?: string | null;
  enabled?: boolean;
}) {
  const t = useTranslations();
  const notification = useNotifications();
  const queryClient = useQueryClient();
  const event = useEventStore((s) => s.event);
  const slotContextRequired =
    parentType === 'Flow' &&
    requiresEventSlotForLiveBinding({
      multiFlow: event?.multiFlow,
      eventSlotId,
    });

  const queryEnabled = enabled && !!parentId && !slotContextRequired;

  const { data: players = [], isLoading } = useGetPlayersQuery(
    {
      entityId: parentId,
      entityType: parentType,
      ...(eventSlotId ? { eventSlotId } : {}),
    },
    { enabled: queryEnabled },
  );

  const playerSocketHandler: PlayerSubscriptionHandler = useMemo(
    () => ({
      onPlayersInvalidated: () => {
        queryClient.invalidateQueries({ queryKey: [GET_PLAYERS_QUERY_KEY] });
        queryClient.invalidateQueries({ queryKey: [GET_AV_SOURCES_QUERY_KEY] });
        queryClient.invalidateQueries({ queryKey: [GET_SPACE_PLAYER_SETUP_QUERY_KEY] });
      },
      onLiveContextInvalidated: () => {
        queryClient.invalidateQueries({ queryKey: [GET_FLOW_QUERY_KEY] });
        queryClient.invalidateQueries({ queryKey: [GET_AV_SOURCES_QUERY_KEY] });
        queryClient.invalidateQueries({ queryKey: [GET_SPACE_PLAYER_SETUP_QUERY_KEY] });
      },
      onPlayerTeardownComplete: (data) => {
        queryClient.invalidateQueries({ queryKey: [GET_PLAYERS_QUERY_KEY] });
        const displayName = data.playerName || `Player ${data.playerId.slice(-4)}`;
        notification.notifySuccess(
          t(
            'player.teardownComplete' as TranslationKey,
            { name: displayName },
            `Media agent "${displayName}" removed successfully`,
          ),
        );
      },
    }),
    [notification, queryClient, t],
  );

  usePlayerSocket(parentId, parentType, playerSocketHandler, queryEnabled);

  const playersByType = useMemo(() => {
    const map = new Map<PlayerType, PlayerDto[]>();
    for (const player of players) {
      const list = map.get(player.type) ?? [];
      list.push(player);
      map.set(player.type, list);
    }
    return map;
  }, [players]);

  return {
    players,
    playersByType,
    isLoading,
    slotContextRequired,
    pairEntityType: parentType,
  };
}
